/**
 * All API calls should be routed through these methods to ensure consistent and centralised exception handling, response data, etc.
 * 
 * This is version 2 of the API, implementing `reject()` in `callApiHandler()`.
 *
 * `callApi2WithOverlay()` is the standard entry point for components: it automatically shows/hides the
 * processing overlay and handles failure toast messages and logging. `callApi1()` (same handling, no
 * overlay), `uploadFile()` and `downloadFile()` (files) are also available. Import this file and call these directly
 * (the Vue app root proxies, e.g. `this.$root.callApi2WithOverlay(...)`, remain for older components).
 */

import config from "./config.js";
import dateHelpers from "./helpers/dateHelpers.js";
import globalStore from "./globalStore.js";
import sharedUtil from "./shared.js";
import logging from "./helpers/logging.js";
import { goToSessionExpired } from "./helpers/session.js";


/**
 * Turn a GET request object into a query string.
 *
 * Values are percent-encoded. They used to be interpolated raw, which worked only for values that
 * happened to contain no URL syntax: an `&` in a filter value split it into a second parameter, a
 * `#` truncated the rest of the query string, a `+` arrived at the API as a space, and a stray `%`
 * could fail the request outright as a malformed escape. Anything already working is unaffected -
 * encoding a value with no reserved characters returns it unchanged.
 *
 * `null` and `undefined` are omitted rather than sent as the literal strings "null"/"undefined",
 * which .NET model binding would bind as those words. An optional filter left unset is therefore
 * simply absent.
 */
function buildQueryString(data)
{
	if (data == null) return "";

	return Object.keys(data)
		.filter((key) => data[key] !== null && data[key] !== undefined)
		.map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
		.join("&");
}


/**
 * Turn a successful file-download response into `{ Blob, FileName, ContentType }`.
 *
 * The name comes from the Content-Disposition header (which the API's CORS policy exposes), preferring the
 * RFC 5987 `filename*=UTF-8''...` form - which carries a non-ASCII name intact - over the plain `filename=`.
 */
function toDownloadedFile(xhttp)
{
	const blob = xhttp.response;
	if (blob == null) return null;

	return {
		Blob: blob,
		FileName: fileNameFromContentDisposition(xhttp.getResponseHeader("content-disposition")),
		ContentType: xhttp.getResponseHeader("content-type") || blob.type || "",
	};
}


function fileNameFromContentDisposition(header)
{
	if (!header) return "";

	const encoded = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(header);
	if (encoded)
	{
		try
		{
			return decodeURIComponent(encoded[2].trim());
		}
		catch
		{
			// A malformed escape - fall back to the plain form below.
		}
	}

	const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
	return plain ? plain[1].trim() : "";
}




const api = {



	/**
     * Call the Lotus back-end API.
     * 
     * @param {string} endpoint The root relative URL of the endpoint to call. Starts with a `/` (e.g. `/Clients/GetClientById`).
     * @param {object} data The data to submit in the request to the API.
     * @param {function} verb The Verb of the Http request - "GET" or "POST". Default is `GET`.
     */
	callApi: function(endpoint, data, verb = "GET")
	{
		return this.callApiHandler("", endpoint, data, verb, false);
	},




	// This is step 1 in migrating callApi() to using api2. We need to change the
	// order of the method signature.
	/**
	 * Call the Lotus back-end API, handling failure toast messages and logging
	 * (like `callApi2WithOverlay()`, but without the processing overlay).
	 *
	 * Import this file into a component and call directly — no need to go through the Vue app root
	 * (`this.$root.callApi1(...)` remains as a proxy to this method for older components).
	 *
	 * This promise always resolves and never rejects: on failure the promise resolves with
	 * `defaultRejectResponse` after the failure has been handled here.
	 *
	 * @param {string} verb HTTP verb to use ("GET" or "POST").
	 * @param {string} endpoint Lotus API endpoint to call.
	 * @param {object} data Request data to send to the API.
	 * @param {object} defaultRejectResponse If the request fails, the value to resolve with. Default is `null`, but set to an expected "no results" value the caller can action without a need for a null check.
	 */
	callApi1: function(verb, endpoint, data, defaultRejectResponse = null)
	{
		var _this = this;

		let promise = new Promise(function(resolve, reject)
		{
			_this.callApi(endpoint, data, verb)
			.then((responseData) => {
				// At this point we are expecting `responseData.status == "success"` returned from the api handler.
				// Anything else will be handled in the `catch()`.
				resolve(responseData.data);
			})
			.catch((responseData) => {
				// Something (anything) failed in the API call.
				_this.handleFailedApiCall(verb, endpoint, data, responseData);

				resolve(defaultRejectResponse);	// Default is `null`
			});

		});

		return promise;
	},





	/**
	 * Call the Lotus back-end API, automatically showing the processing overlay for the
	 * duration of the call, and handling failure toast messages and logging.
	 *
	 * Import this file into a component and call directly — no need to go through the Vue app root
	 * (`this.$root.callApi2WithOverlay(...)` remains as a proxy to this method for older components).
	 *
	 * This promise always resolves and never rejects: the caller is not expected to handle a reject
	 * (that is why this middleware method exists). On failure the promise resolves with
	 * `defaultRejectResponse` after the failure has been handled here.
	 *
	 * @param {string} verb HTTP verb to use ("GET" or "POST").
	 * @param {string} endpoint Lotus API endpoint to call.
	 * @param {object} data Request data to send to the API.
	 * @param {object} defaultRejectResponse If the request fails, the value to resolve with. Default is `null`, but set to an expected "no results" value the caller can action without a need for a null check.
	 */
	callApi2WithOverlay: function(verb, endpoint, data, defaultRejectResponse = null)
	{
		var _this = this;

		sharedUtil.showOverlay();

		let promise = new Promise(function(resolve, reject)
		{
			_this.callApi(endpoint, data, verb)
			.then((responseData) => {
				sharedUtil.hideOverlay();

				// At this point we are expecting `responseData.status == "success"` returned from the api handler.
				// Anything else will be handled in the `catch()`.
				resolve(responseData.data);
			})
			.catch((responseData) => {
				// Something (anything) failed in the API call.

				sharedUtil.hideOverlay();

				_this.handleFailedApiCall(verb, endpoint, data, responseData);

				resolve(defaultRejectResponse);	// Default is `null`
			});

		});

		return promise;
	},




	/**
	 * Upload a file to the main Lotus back-end API (a `multipart/form-data` POST), handling failure
	 * toast messages and logging (no processing overlay).
	 *
	 * Use this for the `/Files/Upload/v1` endpoint. It is the same as `callApi1("POST", ...)`
	 * except the body is sent as the supplied `FormData` rather than as JSON.
	 *
	 * This promise always resolves and never rejects: on failure the promise resolves with
	 * `defaultRejectResponse` after the failure has been handled here.
	 *
	 * @param {string} endpoint The root relative URL of the endpoint to call. Starts with a `/` (e.g. `/Files/Upload/v1`).
	 * @param {FormData} formData The request body. Must contain the file in a field named "file".
	 * @param {object} defaultRejectResponse If the request fails, the value to resolve with. Default is `null`, but set to an expected "no results" value the caller can action without a need for a null check.
	 */
	uploadFile: function(endpoint, formData, defaultRejectResponse = null)
	{
		var _this = this;

		let promise = new Promise(function(resolve, reject)
		{
			_this.callApiHandler("", endpoint, formData, "POST", true)
			.then((responseData) => {
				// At this point we are expecting `responseData.status == "success"` returned from the api handler.
				// Anything else will be handled in the `catch()`.
				resolve(responseData.data);
			})
			.catch((responseData) => {
				// Something (anything) failed in the API call.
				_this.handleFailedApiCall("POST", endpoint, formData, responseData);

				resolve(defaultRejectResponse);	// Default is `null`
			});

		});

		return promise;
	},




	/**
	 * Download a file from the main Lotus back-end API - a GET whose success is the file itself rather than
	 * JSON - handling failure toast messages and logging (no processing overlay).
	 *
	 * Resolves `{ Blob, FileName, ContentType }` on success. Save it, or show it with `URL.createObjectURL()`.
	 *
	 * Only needed for a download that has to carry the auth token (e.g. `/Files/Download/FileName/v1`).
	 * A public file URL such as a `FileRetrievalUrl`, which carries its own access key, can simply be an
	 * `<img src>` or `<a href>`.
	 *
	 * This promise always resolves and never rejects: on failure the promise resolves with
	 * `defaultRejectResponse` after the failure has been handled here.
	 *
	 * @param {string} endpoint The root relative URL of the endpoint to call. Starts with a `/`.
	 * @param {object} data Request data, sent as the query string.
	 * @param {object} defaultRejectResponse If the request fails, the value to resolve with. Default is `null`.
	 */
	downloadFile: function(endpoint, data, defaultRejectResponse = null)
	{
		var _this = this;

		// Only ever resolves (see above), so it takes no `reject`.
		let promise = new Promise(function(resolve)
		{
			_this.callApiHandler("", endpoint, data, "GET", false, true)
			.then((responseData) => {
				// At this point we are expecting `responseData.status == "success"` returned from the api handler.
				// Anything else will be handled in the `catch()`.
				resolve(responseData.data);
			})
			.catch((responseData) => {
				// Something (anything) failed in the API call.
				_this.handleFailedApiCall("GET", endpoint, data, responseData);

				resolve(defaultRejectResponse);	// Default is `null`
			});

		});

		return promise;
	},




	/**
	 * Call an API endpoint and report the outcome to the caller, instead of toasting and logging it.
	 *
	 * The wrappers above are the right choice almost everywhere: a page assumes the happy path and
	 * failure is handled centrally. This one is for the rare case where a failure is *the answer*
	 * rather than an error — the System Admin "API Info" page asks every deployment environment for
	 * its build info at once, and an environment that is down or unreachable is a row on the page,
	 * not six error toasts stacked on top of each other.
	 *
	 * Like the wrappers, this promise always resolves and never rejects. No overlay, no toast, no
	 * Rollbar warning — the caller decides what a failure means.
	 *
	 * @param {string} verb HTTP verb to use ("GET" or "POST").
	 * @param {string} endpoint Lotus API endpoint to call.
	 * @param {object} data Request data to send to the API.
	 * @param {string} baseUrl Base URL (domain) to call, for reaching an API other than the current
	 * environment's. Leave blank to use `config.GetLotusApiBaseUrl()`.
	 * @returns {Promise<object>} `{ isSuccess, data, lotusStatusCode, lotusStatusDetails }`.
	 */
	callApiQuietly: function(verb, endpoint, data, baseUrl = "")
	{
		var _this = this;

		let promise = new Promise(function(resolve)
		{
			_this.callApiHandler(baseUrl, endpoint, data, verb, false)
			.then((responseData) => {
				resolve({
					isSuccess: true,
					data: responseData.data,
					lotusStatusCode: "success",
					lotusStatusDetails: "",
				});
			})
			.catch((responseData) => {
				resolve({
					isSuccess: false,
					data: null,
					lotusStatusCode: responseData.lotusStatusCode,
					lotusStatusDetails: responseData.lotusStatusDetails,
				});
			});
		});

		return promise;
	},




	/**
	 * Shared failure handling for the wrapper methods above (`callApi1()`, `callApi2WithOverlay()`):
	 * show the failure toast message (when one is provided) and log a warning
	 * (when requested).
	 */
	handleFailedApiCall: function(verb, endpoint, data, responseData)
	{
		//-- "Fail" status. Something went wrong and no API data to return.
		if (responseData.failDisplayMessage != "")
		{
			// If there is no title, display the message in the title area instead (looks better in the toasts).
			sharedUtil.showToast({
				type: 'error',
				title: responseData.failDisplayTitle ? responseData.failDisplayTitle : responseData.failDisplayMessage,
				description: responseData.failDisplayTitle ? responseData.failDisplayMessage : "",
				duration: 3000
			});
		}

		if (responseData.logWarning == true)
		{
			logging.logWarning("API request failed",
			{
				ApiVerb: verb,
				ApiEndPoint: endpoint,
				RequestData: data,
				LotusStatusCode: responseData.lotusStatusCode,
				LotusStatusDetails: responseData.lotusStatusDetails,
				ResponseData: responseData
			});
		}
	},





    // Reference: https://www.w3schools.com/xml/ajax_xmlhttprequest_create.asp
    /**
     * Call the Lotus back-end API.
     * 
	 * @param {string} baseUrl Set this to specifically define the based API URL (i.e. the domain) instead of using `config.GetLotusApiBaseUrl()` (which is the default). Leave the value blank/null/falsy to default to `config.GetLotusApiBaseUrl()`.
     * @param {string} endpoint The root relative URL of the endpoint to call. Starts with a `/` (e.g. `/Clients/GetClientById`).
     * @param {object} data The data to submit in the request to the API. For calls to Files API, we asssume this is pre-filled FormData object ready for sending.
     * @param {function} verb The Verb of the Http request - "GET" or "POST". Default is `GET`.
     * @param {boolean} isFileUpload Send `data` as-is (a pre-filled FormData) rather than as JSON.
     * @param {boolean} isFileDownload The endpoint answers success with a file rather than JSON. On success the
     *     resolved `data` is `{ Blob, FileName, ContentType }`. Failures are unchanged: they still arrive as JSON
     *     with the Lotus-Status-Code header, and the branches below decide on the header alone.
     */
    callApiHandler: function(baseUrl, endpoint, data, verb = "GET", isFileUpload = false, isFileDownload = false)
    {

		//-- Handle any fake API calls first (e.g. when developing a new module or feature that does not yet have an actual API).
		var fakeResolveResponse = {
			status: "success",
			data: null,
			lotusStatusCode: "success",
			lotusStatusDetails: "",

			failDisplayTitle: "",
			failDisplayMessage: "",
			logWarning: false
		};


		//-- Calling a live API.

		if (!endpoint.startsWith("/"))
		{
			// URL must start with a "/" to combine for a valid full URL.
			throw "api2.callApiHandler(): endpoint '" + endpoint + "' required to start with a '/' to be able to combine with the domain into a valid full URL.";
		}

		const promise = new Promise(function(resolve, reject) {
	
			let xhttp = new XMLHttpRequest();
			xhttp.onreadystatechange = function() 
			{
				//console.log("api::callApi.onreadystatechange(" + verb + ", " + endpoint + "): xhttp.readyState=", xhttp.readyState, "xhttp.status=", xhttp.status);
				var resolveResponse = {
					status: "fail",
					data: null,
					lotusStatusCode: "",
					lotusStatusDetails: "",

					failDisplayTitle: "",
					failDisplayMessage: "",
					logWarning: false
				}

				// 401 = no valid session ("not-authenticated"), 403 = signed in but refused ("not-authorised").
				// Both carry the Lotus-Status-Code header the branches below decide on.
				if (this.readyState == 4 && (this.status == 200 || this.status == 401 || this.status == 403))
				{
					// console.log("api::callApi(" + verb + ", " + endpoint + "): Response SUCCESS. xhttp=", xhttp);

					let lotusStatusCode = xhttp.getResponseHeader("lotus-status-code");
					let lotusStatusDetails = xhttp.getResponseHeader("lotus-status-details");

					if (!lotusStatusCode && isFileUpload)
					{
						// Initially the Files API does not set these header (because: if just didn't need to in the initial build)
						// so we'll get the values from the special properties in the response data itself.
						const responseData = xhttp.response;
						lotusStatusCode = responseData._LotusStatusCode;
						lotusStatusDetails = responseData._LotusStatusDetails;

						if (lotusStatusCode) lotusStatusCode = lotusStatusCode.toLocaleLowerCase();
					}

					// Carry the answer on the result, so a caller that asks for failures to be reported (`callApiQuietly()`)
					// can tell a refusal ("not-authorised") from any other failure.
					resolveResponse.lotusStatusCode = lotusStatusCode || "";
					resolveResponse.lotusStatusDetails = lotusStatusDetails || "";

					// console.log("api::callApi(" + verb + ", " + endpoint + "): lotusStatusCode=", lotusStatusCode, " | lotusStatusDetails=", lotusStatusDetails);

					// var allHeaders = xhttp.getAllResponseHeaders();
					// console.log("api::callApi(" + verb + ", " + endpoint + "): allHeaders=", allHeaders);
					


					if (lotusStatusCode == "success")
					{
						// API action successfully completed.

						//var responseData = JSON.parse(xhttp.response);    // Nope, response is already an object.
						var responseData = isFileDownload ? toDownloadedFile(xhttp) : xhttp.response;

						if (responseData == null)
						{
							//27 Nov 2025: NOTE - this can happen when retrieving JSON and the JSON has an 
							// 				invalid structure/value in it (e.g. a `NaN` instead of a number).
							
							// Something's not quite right.
							console.warn("api::callApi(" + verb + ", " + endpoint + "): No API response data. RequestData=", data);
							reject(resolveResponse);
						}
						else
						{
							// Data successfull returned from API.
							resolveResponse.data = responseData;
							resolveResponse.status = "success"
							resolve(resolveResponse);
						}
					}
					else if (lotusStatusCode == "not-authenticated")
					{
						// The token this request went out with is no longer good - expired, revoked,
						// or signed out somewhere else. The API is the only thing that can tell us
						// that (portal tokens carry no expiry date), so the verdict is recorded
						// centrally here: globalStore.currentUser.isLoggedIn() reports false from
						// now on, including after a reload.
						//
						// HTTP 401. The API's BaseController answers any request whose header token no
						// longer authenticates anyone this way, before the endpoint runs. The sign-in
						// endpoints are exempt, so it never means bad credentials - always a dead session
						// (expired, signed out elsewhere, or a token from before a database restore).
						console.warn("api::callApi(" + verb + ", " + endpoint + "): Not Authenticated. The auth token is no longer valid.");

						const wasSignedIn = globalStore.currentUser.isLoggedIn();

						globalStore.currentUser.markAuthTokenInvalid();

						if (wasSignedIn)
						{
							// We thought we had a session, so the person is mid-session and goes back to
							// sign in with a "your session has ended" notice rather than a generic error toast.
							// Nothing to show them here - the page is on its way out.
							goToSessionExpired();
						}
						else
						{
							// Nobody was signed in to begin with (e.g. a public page calling an
							// endpoint that needs a session). Fail the call quietly rather than
							// throwing them at a session-expired screen they never earned.
							resolveResponse.failDisplayTitle = "Not Signed In";
							resolveResponse.failDisplayMessage = "Action failed.<br>You need to be signed in to do that.";
						}

						reject(resolveResponse);
					}
					else if (lotusStatusCode == "not-authorised")
					{
						// HTTP 403. A signed-in user who is not allowed to perform the API action.
						console.warn("api::callApi(" + verb + ", " + endpoint + "): Not Authorised. RequestData=", data);

						resolveResponse.failDisplayTitle = "Not Allowed";
						resolveResponse.failDisplayMessage = "Action Failed - Not Allowed.<br>You are not allowed to perform the action";
						
						reject(resolveResponse);
					}
					else if (lotusStatusCode == "failed-validation")
					{
						// Failure validating submitted data.
						// UI should perform all the user friendly validation. Back-end only performs check and stop continued process if failure.
						console.warn("api::callApi(" + verb + ", " + endpoint + "): Failed Validation. RequestData=", data);

						resolveResponse.failDisplayTitle = "Failed Validation";
						resolveResponse.failDisplayMessage = "Action failed.<br>Invalid value provided - this message should only be seen by software developers.<br><br>Details: " + lotusStatusDetails;

						reject(resolveResponse);
					}
					else if (lotusStatusCode == "offline")
					{
						//TODO: I don't think this branch will ever be received. See `else if (xhttp.readyState == 4 && xhttp.status == 0)` below
						console.error("api::callApi(" + verb + ", " + endpoint + "): FAIL. Server response not received.");

						resolveResponse.failDisplayTitle = "Offline";
						resolveResponse.failDisplayMessage = "Action failed.<br>It looks like the beating heart of Lotus AI operations cannot be reached at the moment.<br>Please check your internet connection, or wait a few moments in case this is a temporary interruption, then try again.<br>If you continue to have problems please email <a href='mailto:support@lotusai.co' style='color: blue;'>support@lotusai.co</a>";

						reject(resolveResponse);
					}
					else
					{
						// Some other non-successful result.
						console.warn("api::callApi(" + verb + ", " + endpoint + "): Some other fail result (" + lotusStatusCode + ", " + lotusStatusDetails + "). RequestData=", data);

						resolveResponse.failDisplayTitle = "Something Went Wrong";
						resolveResponse.failDisplayMessage = "Action failed.<br>Something went wrong.<br>Lotus AI developers have been alerted and will investigate as soon as possible.<br>If you do not hear about the issue or would like to contact us, please email <a href='mailto:support@lotusai.co' style='color: blue;'>support@lotusai.co</a>.";
						resolveResponse.logWarning = true;

						reject(resolveResponse);
					}
				}
				else if (xhttp.readyState == 4 && xhttp.status == 0) 
				{
					// The server is offline

					console.error("api::callApi(" + verb + ", " + endpoint + "): FAIL. Server response not received.");

					resolveResponse = {
						status: "fail",
						data: null,
						lotusStatusCode: "offline",
						lotusStatusDetails: "Server repsonse not received",

						failDisplayTitle: "Offline",
						failDisplayMessage: "Action failed.<br>It looks like the beating heart of Lotus AI operations cannot be reached at the moment.<br>Please check your internet connection, or wait a few moments in case this is a temporary interruption, then try again.<br>If you continue to have problems please email <a href='mailto:support@lotusai.co' style='color: blue;'>support@lotusai.co</a>",

						logWarning: false
					}

					reject(resolveResponse);
				}
				else if (this.readyState == 4)
				{
					// The request finished (readyState 4) with a status this handler doesn't have a
					// specific branch for — 404 (endpoint not implemented yet, e.g. a module being
					// built against its contract ahead of the backend), 500, etc. This USED TO
					// fall through to the final `else` below and do nothing, silently leaving the
					// promise pending forever: every caller through callApi2WithOverlay is documented
					// to always resolve (see CLAUDE.md / the JSDoc above), but a stuck promise instead
					// left the processing overlay on screen permanently and the caller's `await` never
					// returning. Found while wiring up the Resource Planning module against endpoints
					// that don't exist in this environment yet — treat it the same as any other
					// non-success Lotus status: reject with a generic failure so the wrapper's
					// `.catch()` can log it and resolve with `defaultRejectResponse` as promised.
					console.warn("api::callApi(" + verb + ", " + endpoint + "): FAIL. Unhandled HTTP status " + this.status + ".");

					reject({
						status: "fail",
						data: null,
						lotusStatusCode: "http-" + this.status,
						lotusStatusDetails: "Unhandled HTTP status " + this.status,

						failDisplayTitle: "Something Went Wrong",
						failDisplayMessage: "Action failed.<br>Something went wrong (HTTP " + this.status + ").<br>Lotus AI developers have been alerted and will investigate as soon as possible.<br>If you do not hear about the issue or would like to contact us, please email <a href='mailto:support@lotusai.co' style='color: blue;'>support@lotusai.co</a>.",

						logWarning: true
					});
				}
				else
				{
					//-- Not finished yet (readyState 0-3). Nothing to do until readyState reaches 4.

					// console.log("api::callApi(" + verb + ", " + endpoint + "): FAIL.", this.readyState, this.status);
				}
	
				/*
				readyState	
					Holds the status of the XMLHttpRequest.
					0: request not initialized
					1: server connection established
					2: request received
					3: processing request
					4: request finished and response is ready
				status	
					200: "OK"
					403: "Forbidden"
					404: "Page not found"
					For a complete list go to the Http Messages Reference (https://www.w3schools.com/tags/ref_httpmessages.asp)
				*/
			};
			
			// console.log("api::callApi(" + verb + ", " + endpoint + "): data=", data);

	
			// If an explicit base URL is supplied then use it, otherwise use the default URL defined in config.
			let url = baseUrl ? baseUrl : config.GetLotusApiBaseUrl();

			// And append the endpoint to have the complete URL to call.
			url = url + endpoint;
	

			if (verb != "GET" && verb != "POST")
			{
				throw "API::callApi(): Unknown verb '" + verb + "' (expecting: `GET` or `POST`)";
			}
	
			// console.log("globalStore.state.currentUser=", JSON.stringify(globalStore.state.currentUser));
			// console.log("globalStore.state.currentUser.authToken=", globalStore.state.currentUser.authToken);

			if (verb == "GET")
			{
				url = url + "?" + buildQueryString(data);

				// console.log("api::callApi(GET: " + url + ")");
			}


			xhttp.open(verb, url, true);
			xhttp.setRequestHeader('Lotus-User-AuthToken', globalStore.state.currentUser.authToken);
			xhttp.setRequestHeader('Lotus-Agent-LocalDateTime', dateHelpers.getLocalDateAndTimeAsIso());
			xhttp.setRequestHeader('Lotus-Agent-LocalDateTimeUtcOffset', dateHelpers.getLocalUtcOffset());
			xhttp.setRequestHeader('Lotus-Portal-RequestingPath', window.location.href);

			//NOTE: [jsnelders, 2024-08-28] This because because I noticed some calls, in some circumstances would retrieve
			//		from the disk cache rather than call the actual endpoint.
			//		E.g. account admin > Users. Manage person. Update their details. "Back" to Users page. On return, it retrieves
			//		from cache and doesn't have the latest updated details.
			// REf: https://stackoverflow.com/questions/22356025/force-cache-control-no-cache-in-chrome-via-xmlhttprequest-on-f5-reload
			xhttp.setRequestHeader("Cache-Control", "no-cache, no-store, max-age=0");

			// A file download has to arrive as a Blob - reading it as JSON would discard it. A failure from the
			// same endpoint is JSON, but its outcome is in the header, so nothing below needs to read it as JSON.
			xhttp.responseType = isFileDownload ? "blob" : "json";

			xhttp.withCredentials = true;	//TODO: This is causing CORS errors on `/EntityHistory/GetLastNForEntity/v1` for some reason.
												// I suspect I added this when testing to use cookies and include the AuthToken in that. Which has it's on cross-origin issues I'm not diving into yet.

			xhttp.timeout = 0;	// Ensure no timeout	(https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/timeout) Also. https://stackoverflow.com/a/46117499/115704

			if (isFileUpload == true)
			{
				//Ref: https://gabrieleromanato.name/javascript-file-upload-with-the-xmlhttprequest-object
				//xhttp.setRequestHeader("Content-Type", "multipart/form-data");		//TODO: *DO NOT* do this, because it doesn't get received properly by .NET in the Files API endpoints.

				// console.log("api::callApi.send(" + verb + ", " + endpoint + ")[isFileUpload]: data=", data);
				// for (const value of data.values()) {
				// 	console.log(   "value=", value);
				// }

				// We send the request to the server with the send() method
				//console.log("api2: xhttp.send(FILE)");
				xhttp.send(data);
			}
			else if (verb == "GET")
			{
				//console.log("api2: xhttp.send(GET)");
				xhttp.send();
			}
			else if (verb == "POST")
			{
				// console.log("api::callApi(POST: " + url + "): data=", data);
	
				const postJsonData = JSON.stringify(data);
	
				// console.log("postJsonData=", postJsonData);

				xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
				//console.log("api2: xhttp.send(POST)");
				xhttp.send(postJsonData);
			}
		});
		
		return promise;
    },



//-- Example usage

// loginRequestData = {};
// api.callApi("/Authenticate/Login", loginRequestData, "POST")
// .then((responseData) => {
// 	console.log("caller(): responseData=", responseData);

// 	// Do something
// })
// .catch( (responseData) => {
// 	console.warn("caller(): Failed. responseData=", responseData);

// 	// No action required. Warning message handled via central API.
// });







    // Source: https://www.w3schools.com/xml/ajax_intro.asp

    /**
     * Call an external API (not part of the Lotus back-end platform).
     * 
     * @param {string} verb HTTP verb to use in the call ("GET" or "POST").
     * @param {string} url The URL of the API to call.
     * @param {object} data The request data to pass to the API.
     * @param {object} headers HTTP headers to send to the API.
     * @param {function} callback Callback function to handle the response to this call. Takes a single parameter of type apiResponse().
     */
    callExternal: function(verb, url, data, headers = null, callback = undefined)
    {
        if (verb != "GET" && verb != "POST")
        {
            throw "API::callExternal(): Unknown verb '" + verb + "' (expecting: GET or POST)";
        }

        var xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function() 
        {
            if (this.readyState == 4 && this.status == 200) 
            {
                // Typical action to be performed when the document is ready:
                //console.log("xhttp.responseText", xhttp.responseText);
                var apiJson = xhttp.response;
                var apiData = JSON.parse(apiJson);

				callback(apiData);
            }
        };


        xhttp.open(verb, url, true);
        // xhttp.setRequestHeader('Access-Control-Allow-Headers', '*');
        // xhttp.setRequestHeader('Access-Control-Allow-Origin', '*');

        if (headers != null)
        {
            for (const header in headers)
            {
                xhttp.setRequestHeader(header, headers[header]);
            }
        }

        xhttp.responseType = "json";

        if (verb == "GET")
        {
			console.log("api2.callExternal(): xhttp.send(GET2)");
            xhttp.send();
        }
        else if (verb == "POST")
        {
            const postJsonData = JSON.stringify(data);
			console.log("api2.callExternal(): xhttp.send(POST2)");
            xhttp.send(postJsonData);
        }
    }
}





export default api;